// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { FacetModel } from "@insite/client-framework/Types/ApiModels";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox/Checkbox";
import CheckboxGroup, { CheckboxGroupProps } from "@insite/mobius/CheckboxGroup/CheckboxGroup";
import Link, { LinkPresentationProps } from "@insite/mobius/Link/Link";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { formatNumber } from "@mcmc/App/Components/FormattedNumber";
import Tooltip from "@mcmc/MobiusOverrides/Tooltip";
import { styledScrollbar } from "@mcmc/XDL/Utilities";
import mergeToNew from "@mcmc/XDL/Utilities/xMergeToNew";
import React, { FC, useEffect, useState } from "react";
import { css } from "styled-components";

export interface ProductListFilterAccordionSectionStyles {
    accordionSection?: AccordionSectionPresentationProps;
    checkBoxGroup?: CheckboxGroupProps;
    checkBox?: CheckboxPresentationProps;
    checkBoxSelected?: CheckboxPresentationProps;
    seeAllLink?: LinkPresentationProps;
    seeLessLink?: LinkPresentationProps;
    contentWrapper?: InjectableCss;
    facetFilterTextField?: TextFieldPresentationProps;
}

export const productListFilterAccordionSectionStyles: ProductListFilterAccordionSectionStyles = {
    accordionSection: {
        headerProps: {
            "aria-level": 1,
            css: css`
                ${getProp("theme.accordion.sectionDefaultProps.headerProps.css")}
                &&&& {
                    button,
                    button:focus {
                        padding: 13px 20px;
                        border-top: none;
                        border-bottom: 1px solid #b7b7b7;
                        border-left: none;
                        border-right: none;
                    }
                }
            `,
        },
        titleTypographyProps: {
            variant: "h6",
            size: "16px",
            lineHeight: "19px",
            forwardAs: "span",
            weight: "bold",
            css: css`
                margin-bottom: 0;
            `,
        },
        panelProps: {
            css: css`
                ${getProp("theme.accordion.sectionDefaultProps.panelProps.css")}
                padding: 10px 10px 10px 15px;
            `,
        },
    },
    checkBoxGroup: {
        css: css`
            ${getProp("theme.checkbox.groupDefaultProps.css")};
            margin: 0 5px;
            padding-bottom: 5px;
            &:last-child {
                padding-bottom: 0px;
            }
        `,
    },
    checkBox: {},
    checkBoxSelected: {},
    seeAllLink: {
        color: "secondary",
        css: css`
            ${getProp("theme.link.defaultProps.css")};
            margin-top: 10px;
            align-self: center;
        `,
    },
    seeLessLink: {
        color: "secondary",
        css: css`
            ${getProp("theme.link.defaultProps.css")};
            margin-top: 10px;
            align-self: center;
        `,
    },
    contentWrapper: {
        css: css`
            overflow-y: auto;
            max-height: 250px;
            padding: 5px 0;
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            ${styledScrollbar()};
        `,
    },
    facetFilterTextField: {
        sizeVariant: "default",
        cssOverrides: {
            formInputWrapper: css`
                ${getProp("theme.formField.defaultProps.cssOverrides.formInputWrapper")};
                width: auto;
                flex: 1;
                margin: 10px 5px;
            `,
            inputSelect: css`
                ${getProp("theme.formField.defaultProps.cssOverrides.inputSelect")};
                background-color: ${getColor("text.inverse")};
                font-size: 14px;
            `,
        },
    },
};

type Facet = FacetModel & {
    tooltip?:
        | string
        | React.DetailedReactHTMLElement<{}, HTMLElement>
        | Array<React.DetailedReactHTMLElement<{}, HTMLElement>>;
    className?: string;
};
export interface Props {
    title: string;
    facets: Facet[];
    onChangeFacet: (facet: FacetModel) => void;
    showMoreLimit: number;
    extendedStyles?: ProductListFilterAccordionSectionStyles;
    expandByDefault: boolean;
    isFilterable?: boolean;
}

const ProductListFiltersAccordionSection: FC<Props> = ({
    title,
    facets,
    onChangeFacet,
    showMoreLimit,
    expandByDefault,
    extendedStyles,
    isFilterable,
}) => {
    const [styles] = React.useState(() => mergeToNew(productListFilterAccordionSectionStyles, extendedStyles));
    const [expanded, setExpanded] = useState(false);
    const [facetFilter, setFacetFilter] = useState("");
    const [filteredFacets, setFilteredFacets] = useState(facets);

    useEffect(() => {
        if (isFilterable) {
            setFilteredFacets(facets.filter(f => f.name.toLowerCase().indexOf(facetFilter.toLowerCase()) >= 0));
        } else {
            setFilteredFacets(facets);
        }
    }, [facets, facetFilter, isFilterable]);

    const limitedFacets = expanded ? filteredFacets : filteredFacets?.slice(0, showMoreLimit);
    const anySelected = filteredFacets.find(f => f.selected) !== undefined;

    return (
        <ManagedAccordionSection
            title={title}
            {...styles.accordionSection}
            initialExpanded={expandByDefault || anySelected}
        >
            {isFilterable && (
                <TextField
                    value={facetFilter}
                    placeholder={translate(`Start Typing to find ${title}`)}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => setFacetFilter(event.target.value)}
                    {...styles.facetFilterTextField}
                />
            )}
            <StyledWrapper {...styles.contentWrapper}>
                {limitedFacets?.map(facet => (
                    <CheckboxGroup key={facet.id} {...styles.checkBoxGroup}>
                        <Checkbox
                            {...(facet.selected ? styles.checkBoxSelected : styles.checkBox)}
                            checked={facet.selected}
                            onChange={() => onChangeFacet(facet)}
                            data-test-selector={`facetFilter${facet.id}`}
                            {...(facet.className
                                ? {
                                      iconProps: {
                                          className: facet.className,
                                      } as any,
                                      typographyProps: {
                                          className: facet.className,
                                      } as any,
                                  }
                                : {})}
                        >
                            {facet.name} {facet.count !== -1 && `(${formatNumber(facet.count)})`}
                            {facet.tooltip && <Tooltip text={facet.tooltip} placement="top" />}
                        </Checkbox>
                    </CheckboxGroup>
                ))}
                {filteredFacets &&
                    filteredFacets.length > showMoreLimit &&
                    (expanded ? (
                        <Link {...styles.seeLessLink} onClick={() => setExpanded(false)}>
                            {translate("See Less")}
                        </Link>
                    ) : (
                        <Link {...styles.seeAllLink} onClick={() => setExpanded(true)}>
                            {translate("See All")}
                        </Link>
                    ))}
            </StyledWrapper>
        </ManagedAccordionSection>
    );
};

export default ProductListFiltersAccordionSection;
